import type {
  BuyAction,
  Brand,
  PropertyValue,
  UnitPriceSpecification,
  Organization,
  Offer,
  CollectionPage,
  BreadcrumbList,
  OfferCatalog,
  Product,
} from "schema-dts";

import { ProductData } from "../types/schema.types";

/**
 * Generates a BuyAction schema object for product purchase actions
 * @param buyActionUrl - The URL template for the buy action
 * @returns BuyAction schema object
 */
export function generateBuyAction(buyActionUrl: string): BuyAction {
  return {
    "@type": "BuyAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: buyActionUrl,
      actionPlatform: [
        "https://schema.org/DesktopWebPlatform",
        "https://schema.org/MobileWebPlatform",
      ],
    },
  };
}

/**
 * Generates a PropertyValue schema object for additional product properties
 * @param name - The property name
 * @param value - The property value
 * @returns PropertyValue schema object
 */
export function generatePropertyValue(
  name: string,
  value: string
): PropertyValue {
  return {
    "@type": "PropertyValue",
    name,
    value,
  };
}

/**
 * Generates a Brand schema object with optional URL
 * @param name - The brand name
 * @param url - Optional brand URL
 * @returns Brand schema object
 */
export function generateBrand(name: string, url?: string): Brand {
  const brand: Brand = {
    "@type": "Brand",
    name,
  };

  if (url) {
    brand.url = url;
  }

  return brand;
}

/**
 * Generates a UnitPriceSpecification schema object
 * @param price - The price value
 * @param currency - The currency code
 * @param priceType - The price type (e.g., "SalePrice", "ListPrice")
 * @returns UnitPriceSpecification schema object
 */
export function generateUnitPriceSpecification(
  price: number,
  currency: string,
  priceType: "SalePrice" | "ListPrice" = "SalePrice"
): UnitPriceSpecification {
  return {
    "@type": "UnitPriceSpecification",
    price,
    priceCurrency: currency,
    priceType: `https://schema.org/${priceType}`,
  };
}

/**
 * Generates an array of UnitPriceSpecification objects for sale and list prices
 * @param salePrice - The sale price
 * @param currency - The currency code
 * @param listPrice - Optional list price (regular price)
 * @returns Array of UnitPriceSpecification objects
 */
export function generatePriceSpecifications(
  salePrice: number,
  currency: string,
  listPrice?: number
): UnitPriceSpecification[] {
  const specifications: UnitPriceSpecification[] = [
    generateUnitPriceSpecification(salePrice, currency, "SalePrice"),
  ];

  if (listPrice) {
    specifications.push(
      generateUnitPriceSpecification(listPrice, currency, "ListPrice")
    );
  }

  return specifications;
}

/**
 * Generates an Organization schema object for sellers
 * @param name - The organization name
 * @param url - Optional organization URL
 * @returns Organization schema object
 */
export function generateSeller(name: string, url?: string): Organization {
  const seller: Organization = {
    "@type": "Organization",
    name,
  };

  if (url) {
    seller.url = url;
  }

  return seller;
}

/**
 * Adds a PropertyValue to the additionalProperty array of a schema object
 * @param schema - The schema object to add the property to
 * @param name - The property name
 * @param value - The property value
 */
export function addAdditionalProperty(
  schema: { additionalProperty?: any },
  name: string,
  value: string
): void {
  const existingProps = Array.isArray(schema.additionalProperty)
    ? schema.additionalProperty
    : schema.additionalProperty
    ? [schema.additionalProperty]
    : [];

  schema.additionalProperty = [
    ...(existingProps as PropertyValue[]),
    generatePropertyValue(name, value),
  ];
}

/**
 * Generates an Offer schema object for product offers
 * @param price - The offer price
 * @param currency - The currency code
 * @param availability - The availability status
 * @param url - The offer URL
 * @param regularPrice - Optional regular price for price specifications
 * @param sellerName - The seller organization name
 * @param sellerUrl - Optional seller URL
 * @param sku - Optional SKU for the specific offer
 * @param size - Optional size for the specific offer
 * @returns Offer schema object
 */
export function generateOffer(
  price: number,
  currency: string,
  availability: "InStock" | "OutOfStock" | "LimitedAvailability",
  url: string,
  regularPrice?: number,
  sellerName = "Otrium",
  sellerUrl?: string,
  sku?: string,
  size?: string
): Offer {
  const offer: Offer = {
    "@type": "Offer",
    price,
    priceCurrency: currency,
    availability: `https://schema.org/${availability}`,
    url,
    priceSpecification: generatePriceSpecifications(
      price,
      currency,
      regularPrice
    ),
    seller: generateSeller(sellerName, sellerUrl),
  };

  // Add SKU if provided
  if (sku) {
    offer.sku = sku;
  }

  // Add size as additional property if provided
  if (size) {
    addAdditionalProperty(offer, "size", size);
  }

  return offer;
}

/**
 * Generates a CollectionPage schema object for collection/brand pages
 * @param name - The collection page name
 * @param description - The collection page description
 * @param url - The collection page URL
 * @param inLanguage - Optional language code for the page
 * @param breadcrumbs - Optional breadcrumb items to include in the page
 * @returns CollectionPage schema object
 */
export function generateCollectionPage(
  name: string,
  description: string,
  url: string,
  inLanguage?: string,
  breadcrumbs?: Array<{ name: string; url?: string }>
): CollectionPage {
  const collectionPage: CollectionPage = {
    "@type": "CollectionPage",
    name,
    description,
    url,
  };

  if (inLanguage) {
    collectionPage.inLanguage = inLanguage;
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbList: BreadcrumbList = {
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((item, index) => {
        const breadcrumbItem: {
          "@type": "ListItem";
          position: number;
          name: string;
          item?: string;
        } = {
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
        };

        // Only add URL if it exists (last item typically doesn't have URL)
        if (item.url) {
          breadcrumbItem.item = item.url;
        }

        return breadcrumbItem;
      }),
    };

    collectionPage.breadcrumb = breadcrumbList;
  }

  return collectionPage;
}

/**
 * Generates a Product schema object from ProductData
 * @param productData - Product information for schema generation
 * @returns Product schema object
 */
export function generateProduct(productData: ProductData): Product {
  const offers: Offer[] = [];

  // Add offers for each variation if they exist
  if (productData.variations && productData.variations.length > 0) {
    productData.variations.forEach((variation) => {
      offers.push(
        generateOffer(
          variation.price,
          productData.currency,
          variation.availability,
          productData.url,
          productData.regularPrice,
          productData.seller,
          productData.sellerUrl,
          variation.sku,
          variation.size
        )
      );
    });
  } else {
    // If no variations, use main product data
    offers.push(
      generateOffer(
        productData.price,
        productData.currency,
        productData.availability,
        productData.url,
        productData.regularPrice,
        productData.seller,
        productData.sellerUrl,
        productData.sku,
        productData.size
      )
    );
  }

  const product: Product = {
    "@type": "Product",
    name: productData.name,
    description: productData.description,
    image: productData.images,
    sku: productData.sku,
    offers: offers.length > 1 ? offers : offers[0],
    url: productData.url,
  };

  // Add brand information
  if (productData.brandUrl) {
    product.brand = generateBrand(productData.brand, productData.brandUrl);
  } else {
    product.brand = generateBrand(productData.brand);
  }

  // Add optional fields if they exist
  if (productData.gtin) {
    product.gtin = productData.gtin;
  }

  if (productData.mpn) {
    product.mpn = productData.mpn;
  }

  if (productData.color) {
    product.color = productData.color;
  }

  if (productData.material) {
    product.material = productData.material;
  }

  if (productData.size) {
    product.size = productData.size;
  }

  if (productData.condition) {
    product.itemCondition = productData.condition;
  }

  if (productData.category) {
    product.category = productData.category;
  }

  if (productData.careInstructions) {
    addAdditionalProperty(
      product,
      "careInstructions",
      productData.careInstructions
    );
  }

  if (productData.buyActionUrl) {
    product.potentialAction = generateBuyAction(productData.buyActionUrl);
  }

  return product;
}

/**
 * Generates an OfferCatalog schema object for product listing pages
 * @param name - The catalog name
 * @param description - The catalog description
 * @param url - The catalog URL
 * @param products - Array of ProductData objects to include in the catalog
 * @param inLanguage - Optional language code for the catalog
 * @param breadcrumbs - Optional breadcrumb items to include in the catalog
 * @returns OfferCatalog schema object
 */
export function generateOfferCatalog(
  name: string,
  description: string,
  url: string,
  products: ProductData[],
  breadcrumbs?: Array<{ name: string; url?: string }>
): OfferCatalog {
  const offerCatalog: OfferCatalog = {
    "@type": "OfferCatalog",
    name,
    description,
    url,
  };

  // Convert products to Product schema objects using the reusable function
  const productSchemas: Product[] = products.map((productData) =>
    generateProduct(productData)
  );

  offerCatalog.numberOfItems = products.length;
  offerCatalog.itemListElement = productSchemas;

  // Add breadcrumbs if provided
  //   if (breadcrumbs && breadcrumbs.length > 0) {
  //     const breadcrumbList: BreadcrumbList = {
  //       "@type": "BreadcrumbList",
  //       itemListElement: breadcrumbs.map((item, index) => {
  //         const breadcrumbItem: {
  //           "@type": "ListItem";
  //           position: number;
  //           name: string;
  //           item?: string;
  //         } = {
  //           "@type": "ListItem",
  //           position: index + 1,
  //           name: item.name,
  //         };

  //         // Only add URL if it exists (last item typically doesn't have URL)
  //         if (item.url) {
  //           breadcrumbItem.item = item.url;
  //         }

  //         return breadcrumbItem;
  //       }),
  //     };

  //     offerCatalog.breadcrumb = breadcrumbList;
  //   }

  return offerCatalog;
}
